import { useContext, useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { MyContext } from '../../context';
import DataTable from '../../components/tables/DataTable';
import Modal from '../../components/modal';
import Pagination from '../../components/Pagination';
import {
	listFulfillmentRouting,
	createFulfillmentRouting,
	updateFulfillmentRouting,
	removeFulfillmentRouting,
	listProducts,
} from '../../utils/queries';

// Products list is capped at 200 per request (see productsAdmin.service.ts
// listProducts) -- fine for this dropdown at current catalog sizes, but a
// store with more products than that would need a searchable picker instead.
const PRODUCT_DROPDOWN_LIMIT = 200;

const PAGE_SIZE = 20;

// No shared source of truth with the storefront's custom-fulfillment form
// registry -- add a key here only after the matching form module exists
// there, and keep this list in sync by hand.
const FORM_SCHEMA_KEYS = ['prescription_lens_v1'];

const emptyForm = {
	productNumber: '',
	sku: '',
	fulfillmentType: 'PHOENIX_STANDARD',
	deliveryMethod: 'EMAIL_IMMEDIATE',
	partnerName: '',
	partnerEmail: '',
	formSchemaKey: '',
};

export default function FulfillmentRoutingList() {
	const { activeStoreId } = useContext(MyContext);
	const [rows, setRows] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [products, setProducts] = useState([]);

	useEffect(() => {
		if (!activeStoreId) return;
		listProducts({ storeId: activeStoreId, limit: PRODUCT_DROPDOWN_LIMIT }).then((result) => {
			if (result?.success) setProducts(result.data.products);
		});
	}, [activeStoreId]);

	const productByNumber = new Map(products.map((p) => [p.productNumber, p]));
	const productLabel = (productNumber) => {
		const product = productByNumber.get(productNumber);
		return product ? `${product.displayName || product.name} (${productNumber})` : productNumber;
	};

	const refresh = () => {
		if (!activeStoreId) return;
		setLoading(true);
		listFulfillmentRouting({
			storeId: activeStoreId,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			productNumber: search || undefined,
		}).then((result) => {
			if (result?.success) {
				setRows(result.data.fulfillmentRouting);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeStoreId, page, search]);

	const openCreate = () => {
		setEditing(null);
		setForm(emptyForm);
		setError('');
		setOpen(true);
	};

	const openEdit = (row) => {
		setEditing(row);
		setForm({
			productNumber: row.productNumber,
			sku: row.sku || '',
			fulfillmentType: row.fulfillmentType,
			deliveryMethod: row.deliveryMethod || 'EMAIL_IMMEDIATE',
			partnerName: row.partnerName || '',
			partnerEmail: row.partnerEmail || '',
			formSchemaKey: row.formSchemaKey || '',
		});
		setError('');
		setOpen(true);
	};

	const buildPayload = () => {
		const isPartnerLab = form.fulfillmentType === 'PARTNER_LAB';
		return {
			productNumber: form.productNumber,
			sku: form.sku || null,
			fulfillmentType: form.fulfillmentType,
			deliveryMethod: isPartnerLab ? form.deliveryMethod : null,
			partnerName: isPartnerLab ? form.partnerName || null : null,
			partnerEmail: isPartnerLab ? form.partnerEmail || null : null,
			formSchemaKey: isPartnerLab ? form.formSchemaKey || null : null,
		};
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		const result = editing
			? await updateFulfillmentRouting(editing.productFulfillmentRoutingId, buildPayload())
			: await createFulfillmentRouting({ ...buildPayload(), storeId: activeStoreId });
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setOpen(false);
		refresh();
	};

	const handleRemove = async (id) => {
		await removeFulfillmentRouting(id);
		refresh();
	};

	const columns = [
		{
			key: 'productNumber',
			header: 'Product',
			render: (value, row) => (
				<button
					type='button'
					className='block w-full text-left text-black font-medium hover:underline'
					onClick={() => openEdit(row)}
				>
					{productLabel(value)}
				</button>
			),
		},
		{ key: 'sku', header: 'SKU', render: (value) => value || 'All variants' },
		{ key: 'fulfillmentType', header: 'Fulfillment Type' },
		{ key: 'deliveryMethod', header: 'Delivery Method', render: (value) => value || '-' },
		{ key: 'partnerName', header: 'Partner', render: (value) => value || '-' },
		{
			key: 'productFulfillmentRoutingId',
			header: '',
			sortable: false,
			render: (id) => (
				<button type='button' onClick={() => handleRemove(id)}>
					<Trash2 size={20} />
				</button>
			),
		},
	];

	const isPartnerLab = form.fulfillmentType === 'PARTNER_LAB';

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-3'>
				<h1 className='text-2xl font-light tracking-tight'>Custom Fulfillment</h1>
				<button type='button' className='btn-primary' disabled={!activeStoreId} onClick={openCreate}>
					New Fulfillment
				</button>
			</div>

			<p className='text-gray-400'>Add custom fulfillment steps to run after the standard fulfillment (ERP).</p>

			<div className='my-5'>
				<label>Search</label>
				<div className='relative max-w-sm'>
					<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
					<input
						className='w-52 form-input pl-9'
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(0);
						}}
					/>
				</div>
			</div>

			<DataTable columns={columns} data={rows} loading={loading} emptyHeading='No custom fulfillment yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />

			<Modal open={open} setOpen={setOpen} title={editing ? 'Edit Fulfillment' : 'New Fulfillment'}>
				<form onSubmit={handleSubmit} className='space-y-4'>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<div>
						<label>Product</label>
						<select
							className='form-select'
							required
							value={form.productNumber}
							onChange={(e) => setForm({ ...form, productNumber: e.target.value })}
						>
							<option value='' disabled>
								Select a product...
							</option>
							{/* If editing a row whose product isn't in the fetched list (inactive, or
							    beyond the dropdown's fetch limit), keep its current value selectable
							    rather than silently blanking it out. */}
							{form.productNumber && !productByNumber.has(form.productNumber) && (
								<option value={form.productNumber}>{productLabel(form.productNumber)}</option>
							)}
							{products.map((p) => (
								<option key={p.productNumber} value={p.productNumber}>
									{p.displayName || p.name} ({p.productNumber})
								</option>
							))}
						</select>
					</div>
					<div>
						<label>SKU (leave blank to apply to all variants)</label>
						<input
							className='form-input'
							value={form.sku}
							onChange={(e) => setForm({ ...form, sku: e.target.value })}
						/>
					</div>
					<div>
						<label>Fulfillment Type</label>
						<select
							className='form-select'
							value={form.fulfillmentType}
							onChange={(e) => setForm({ ...form, fulfillmentType: e.target.value })}
						>
							<option value='PHOENIX_STANDARD'>Standard (ERP)</option>
							<option value='PARTNER_LAB'>Partner Lab</option>
						</select>
					</div>

					{isPartnerLab && (
						<>
							<div>
								<label>Delivery Method</label>
								<select
									className='form-select'
									value={form.deliveryMethod}
									onChange={(e) => setForm({ ...form, deliveryMethod: e.target.value })}
								>
									<option value='EMAIL_IMMEDIATE'>Email (immediate, per order)</option>
									<option value='WEEKLY_EXPORT'>Weekly export</option>
								</select>
							</div>
							<div>
								<label>Partner Name</label>
								<input
									className='form-input'
									value={form.partnerName}
									onChange={(e) => setForm({ ...form, partnerName: e.target.value })}
								/>
							</div>
							<div>
								<label>Partner Email</label>
								<input
									type='email'
									className='form-input'
									required
									value={form.partnerEmail}
									onChange={(e) => setForm({ ...form, partnerEmail: e.target.value })}
								/>
							</div>
							<div>
								<label>Form Schema</label>
								<select
									className='form-select'
									required
									value={form.formSchemaKey}
									onChange={(e) => setForm({ ...form, formSchemaKey: e.target.value })}
								>
									<option value='' disabled>
										Select a form...
									</option>
									{FORM_SCHEMA_KEYS.map((key) => (
										<option key={key} value={key}>
											{key}
										</option>
									))}
								</select>
							</div>
						</>
					)}

					<div className='flex gap-3 pt-2'>
						<button type='submit' disabled={saving} className='btn-primary'>
							{saving ? 'Saving...' : 'Save'}
						</button>
						<button type='button' className='btn-secondary' onClick={() => setOpen(false)}>
							Cancel
						</button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
